/* eslint-disable no-constant-condition */
import { all, call, fork, takeLatest, take, cancelled, cancel, select, put } from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import { isEmpty } from 'lodash';

import {
  START_TELEMETRY_POLLING,
  STOP_TELEMETRY_POLLING,
} from 'constants/ActionTypes';
import { CustomAction } from 'model/state';
import { refreshUnitDetailsSuccess, UnitPayload } from 'actions';
import { getLocationById } from '../api/Location';
import { selectUserId, selectUserUnitSystem } from '../selectors';
import { toUnit } from '../model/api';
import { UnitSystem } from '../model';

const POLLING_INTERVAL = 5000; // 5 sec
const MAX_POLLING_TIME = 1800000; // max time requesting telemetry in the same page - 30 mins

function createTelemetryPollChannel() {
  return eventChannel((emitter) => {
    let total = 0;
    const iv = setInterval(() => {
      total += POLLING_INTERVAL;
      emitter(total < MAX_POLLING_TIME);
    }, POLLING_INTERVAL);
    // The subscriber must return an unsubscribe function
    return () => {
      clearInterval(iv);
    };
  });
}

function* startTelemetryPollingRequest(action: CustomAction<UnitPayload>) {
  const unitId = action.payload?.unitId as string;
  // creates the channel
  const pollChannel = yield call(createTelemetryPollChannel);

  try {

    let isActive = true;
    while (isActive) {
      isActive = yield take(pollChannel);
      try {
        const unit = yield call(getLocationById, unitId, ['devices', 'parent']);
        const userUnitSystem = yield select(selectUserUnitSystem());
        const authUserId = yield select(selectUserId());
        yield put(refreshUnitDetailsSuccess(toUnit(unit, userUnitSystem || UnitSystem.IMPERIAL, authUserId)));
      } catch (error) {
        console.error(error);
      }
    }
    pollChannel.close();
  } finally {
    if (yield cancelled()) {
      pollChannel.close();
    }
  }
}

function* startTelemetryPollingWorker(action: CustomAction<UnitPayload>) {
  try {
    if (isEmpty(action.payload?.unitId)) {
      throw Error('Invalid Parameters');
    }

    const task = yield fork(startTelemetryPollingRequest, action);

    yield take(STOP_TELEMETRY_POLLING);
    yield cancel(task);
  } catch (error) {
    console.error('An Error occurred while starting telemetry polling');
  }
}

export function* startTelemetryPollingSaga() {
  yield takeLatest(START_TELEMETRY_POLLING, startTelemetryPollingWorker);
}

export default function* telemetrySaga() {
  yield all([
    fork(startTelemetryPollingSaga),
  ]);
}
